import { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getChannelById } from '../../models/channels/selectors';
import ViewTrigger from '../../components/view-trigger';

import ContentLayoutGallery from '../../components/v2/content-layouts/content-layout-gallery';
import ContentLayoutRow from '../../components/v2/content-layouts/content-layout-row';
import ContentLayoutGrid from '../../components/v2/content-layouts/content-layout-grid';
import ContentLayoutHero, {
  heroTierItemCount,
} from '../../components/v2/content-layouts/content-layout-hero';
import { DEFAULT_SKIP_TO_CONTENT_ID } from '../../components/skip-to-content/skip-to-content';
import {
  InitiativeTagResponse,
  useInitiativeTagsQuery,
} from '../../common/queries/use-initiative-tags-query';
import { Content } from '../../models/content/types';
import { Channel } from '../../models/channels/types';
import { RootPatronState } from '../../common/use-patron-selector';
import {
  ContentHomepageModule,
  ContentModuleSettings,
} from '../../models/home-page-custom/types';
import {
  HomepageFeedQueryConfig,
  useHomepageFeedByModulesQuery,
} from '../../common/queries/use-homepagefeed-query';
import {
  MAX_DISPLAY_CONTENTS_MAP,
  isDisplayType,
} from '../../common/queries/use-content-feed-query';
import useScreenSize from '../../common/use-screen-size';
import useFeatureFlag from '../../common/use-feature-flag';
import { Feature } from '../../models/features/features';
import { CustomHomepageModuleContext } from './custom-homepage-module-context';

/** The module types with a mobile-layouts treatment. */
const MOBILE_LAYOUT_DISPLAY_TYPES: ContentModuleSettings['display_type'][] = [
  'hero',
  'grid',
  'row',
];

/** Fetch's `per_page` is sized for the widest tier unconditionally, so this
 * must stay 4 outside the tablet hero tier, or later modules lose items to
 * dedup. */
const maxDisplayContentsFor = (
  displayType: ContentModuleSettings['display_type'],
  isTabletHero: boolean
): number => {
  if (displayType === 'hero') return heroTierItemCount(isTabletHero);
  if (isDisplayType(displayType)) {
    return MAX_DISPLAY_CONTENTS_MAP[displayType];
  }
  return 0;
};

/**
 * Props for the HomeFeedSection component
 */
type HomeFeedSectionProps = {
  order: number;
  contentModule: ContentHomepageModule;
  feedItems: Content[];
  isLoading: boolean;
  isLoaded: boolean;
  onFetch: () => void;
  initiativeTags: InitiativeTagResponse[];
  homepageId: string;
};

/**
 * HomeFeedSection component that renders a single feed section
 */
const HomeFeedSection = ({
  order,
  contentModule,
  feedItems,
  isLoading,
  isLoaded,
  onFetch,
  initiativeTags,
  homepageId,
}: HomeFeedSectionProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { width, isMobile, isMobileSmall } = useScreenSize();
  const mobileWebParity = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_PARITY
  );
  const mobileWebLayouts = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_LAYOUTS
  );
  const isTabletHero = isMobile && !!mobileWebLayouts && !isMobileSmall;

  // for now, we only support single reference ID, even though the homepage module returns an array.
  // 'only_include_followed_topics' and 'content_inclusion' are honoured on the module feed,
  // but no generic feed page reproduces the filter, so either one forces the per-module
  // /homepage/:id/feed/:moduleId "see all" route below.

  const {
    source,
    reference_ids,
    only_include_followed_topics,
    content_inclusion,
    display_type: displayType,
    hero_layout: heroLayout,
    minimum_content_threshold: minContents,
  } = contentModule.attributes.settings ?? {};

  // The .container class adds 20px padding that squeezes horizontal feed cards
  // at widths just above the mobile breakpoint (800px). Switch to the mobile
  // container 40px early to reclaim that space and prevent card image overflow.
  const useMobileContainer = width < 840 && !!mobileWebParity;
  // The mobile layouts go further: their scroll rows supply their own gutter and
  // have to reach the viewport, so the module drops all page padding. Gallery
  // has no mobile-layouts treatment, so it keeps .container's inset.
  const useEdgeToEdgeContainer =
    isMobile &&
    !!mobileWebLayouts &&
    MOBILE_LAYOUT_DISPLAY_TYPES.includes(displayType);

  const detailedHeroEnabled = !!useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_DETAILED_HERO
  );
  const heroDetailed =
    displayType === 'hero' && heroLayout === 'detailed' && detailedHeroEnabled;

  const manualFetch = order > 1;
  const channel = useSelector((state: RootPatronState) =>
    getChannelById(state, reference_ids?.[0]?.toString() || '')
  ) as Channel | undefined;

  // Find matching initiative tag by converting filterId to string for comparison
  const initiative = initiativeTags.find(
    (tag) => tag.id === String(reference_ids?.[0])
  )?.attributes;

  const displayTitle = contentModule.attributes.name;
  const displayDescription = contentModule.attributes.settings?.description;

  const sectionId = `module-${contentModule.id}`;

  const viewAllText = t('landing.see_all');

  //used if there is no existing feed page that exactly matches the module source.
  //we have no feed that handles multiple topics/initiatives, nor the options relating to submission source or following topics.
  const fallbackViewAllUrl = `/homepage/${homepageId}/feed/${contentModule.id}`;

  const viewAllUrl = (): string => {
    if (
      (reference_ids && reference_ids?.length > 1) ||
      only_include_followed_topics ||
      content_inclusion
    ) {
      return fallbackViewAllUrl;
    }
    if (source === 'topic') return `/channels/${reference_ids?.[0]}`;
    if (source === 'initiative') return `/initiatives/${reference_ids?.[0]}`;
    return `/feed/${source}`;
  };

  const shouldDisplay = (): boolean => {
    // The home feed component assumes an array of content with length > 1
    // Adding defensive check for uninitialized data coming from server
    let result = feedItems.length >= (minContents || 1);

    if (source === 'initiative') {
      result = result && initiative !== undefined && initiative.is_active;
    }

    if (source === 'topic') {
      result = result && channel !== undefined;
    }

    return result;
  };

  // For sections beyond the first, show ViewTrigger for lazy loading
  if (manualFetch && !isLoaded) {
    return <ViewTrigger offset="200px" onInview={onFetch} />;
  }

  if (!shouldDisplay()) {
    return null;
  }

  if (!sectionId) {
    return null;
  }

  // Build base common props for all feed components
  const baseProps = {
    id: sectionId,
    title: displayTitle,
    description: displayDescription,
    seeAllText: viewAllText,
    viewAllText,
    viewAllContext: displayTitle,
    feedUrl: viewAllUrl(),
    contents: feedItems,
    analyticsData: { location: `home_${source}` },
    isTopic: source === 'topic',
  };

  return (
    <CustomHomepageModuleContext.Provider value={contentModule.id}>
      <section
        aria-labelledby={`${baseProps.id}--title`}
        aria-describedby={
          baseProps.description ? `${baseProps.id}--description` : undefined
        }
      >
        <div
          className={
            useEdgeToEdgeContainer
              ? 'home__feed-section--edge-to-edge'
              : useMobileContainer
              ? 'home__feed-section--mobile'
              : 'container'
          }
        >
          {displayType === 'gallery' && <ContentLayoutGallery {...baseProps} />}
          {displayType === 'hero' && (
            <ContentLayoutHero
              {...baseProps}
              detailed={heroDetailed}
              tier={isTabletHero ? 'tablet' : 'default'}
              mobileWebLayouts={!!mobileWebLayouts}
              mobileWebParity={!!mobileWebParity}
            />
          )}
          {displayType === 'grid' && (
            <ContentLayoutGrid
              {...baseProps}
              minLength={minContents || 1}
              maxLength={6}
              isFetching={isLoading}
              mobileWebLayouts={!!mobileWebLayouts}
              mobileWebParity={!!mobileWebParity}
            />
          )}
          {displayType === 'row' && (
            <ContentLayoutRow
              {...baseProps}
              minLength={minContents || 1}
              isFetching={isLoading}
              mobileWebLayouts={!!mobileWebLayouts}
              mobileWebParity={!!mobileWebParity}
            />
          )}
        </div>
      </section>
    </CustomHomepageModuleContext.Provider>
  );
};

/**
 * Deduplicates content arrays based on content IDs
 *
 * @param contentsArr - Array of content arrays to deduplicate
 * @param lengthArr - Array of max lengths for each content array
 * @returns Deduplicated array of content arrays
 */
const dedupContents = (
  contentsArr: Content[][],
  lengthArr: number[]
): Content[][] => {
  const flattenContentIds = (contents: Content[][]) =>
    Array.prototype.concat
      .apply([] as Content[], contents)
      .map((c: Content) => c.id);

  return contentsArr.reduce((arr, contents, idx) => {
    const curIds = flattenContentIds(arr);
    const uniqContents = contents
      .filter((c) => !curIds.includes(c.id))
      .slice(0, lengthArr[idx]);

    return [...arr, uniqContents];
  }, [] as Content[][]);
};

/**
 * Props for the HomeFeeds component
 */
type HomeFeedsProps = {
  homepageId: string;
  contentModules: ContentHomepageModule[];
};

/**
 * HomeFeeds is the main component that displays all feed sections on the home page
 */
export const HomeFeeds = ({
  homepageId,
  contentModules,
}: HomeFeedsProps): JSX.Element | null => {
  const {
    width: homeFeedsWidth,
    isMobile: homeFeedsIsMobile,
    isMobileSmall: homeFeedsIsMobileSmall,
  } = useScreenSize();
  const homeFeedsMobileWebParity = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_PARITY
  );
  const homeFeedsMobileWebLayouts = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_LAYOUTS
  );
  const useCompactGap = homeFeedsWidth < 840 && !!homeFeedsMobileWebParity;
  const homeFeedsIsTabletHero =
    homeFeedsIsMobile && !!homeFeedsMobileWebLayouts && !homeFeedsIsMobileSmall;
  // Extract filter IDs for initiative tags query
  const initiativeFilterIds = useMemo(() => {
    return contentModules
      .filter(
        (mod) =>
          mod.attributes.settings?.source === 'initiative' &&
          !!mod.attributes.settings?.reference_ids
      )
      .flatMap((mod) => mod.attributes.settings?.reference_ids as number[])
      .map((id) => String(id));
  }, [contentModules]);

  // Use React Query to fetch initiative tags
  const { data: initiativeTagsData = [] } = useInitiativeTagsQuery(
    initiativeFilterIds.length > 0 ? initiativeFilterIds : undefined
  );

  // Track which sections should have their queries enabled
  // Start with first two sections enabled (order 0 and 1)
  const [enabledSections, setEnabledSections] = useState<Set<number>>(
    new Set([0, 1])
  );

  // Prepare query configs for all modules
  const queryConfigs = useMemo<HomepageFeedQueryConfig[]>(() => {
    return contentModules.map((contentModule, order) => {
      // Only enable queries for sections in enabledSections
      const enabled = enabledSections.has(order);
      return {
        contentModule,
        order,
        enabled,
      };
    });
  }, [contentModules, enabledSections]);

  // Fetch all module feeds in parallel
  const feedQueries = useHomepageFeedByModulesQuery(queryConfigs);

  // Handler to enable a section's query when it's triggered
  const handleSectionFetch = useCallback(
    (order: number, refetch: () => void) => {
      // Enable the query for this section
      setEnabledSections((prev) => new Set(prev).add(order));
      // Refetch will be called automatically when enabled changes,
      // but we can also call it explicitly for immediate fetch
      refetch();
    },
    []
  );

  const allFeedItems = feedQueries.map((query) => query.data || []);
  const maxContents = queryConfigs.map((config) =>
    maxDisplayContentsFor(
      config.contentModule.attributes.settings?.display_type,
      homeFeedsIsTabletHero
    )
  );

  // Deduplicate contents across sections
  const deduplicatedContents = dedupContents(allFeedItems, maxContents);

  return (
    <div
      id={DEFAULT_SKIP_TO_CONTENT_ID}
      className={`home__feed-sections${
        useCompactGap ? ' home__feed-sections--compact' : ''
      }`}
    >
      {contentModules.map((content_module, idx) => {
        const query = feedQueries[idx];
        return (
          <HomeFeedSection
            key={idx}
            order={idx}
            homepageId={homepageId}
            contentModule={content_module}
            feedItems={deduplicatedContents[idx] || []}
            isLoading={query.isLoading}
            isLoaded={enabledSections.has(idx) && query.isSuccess}
            onFetch={() => handleSectionFetch(idx, query.refetch)}
            initiativeTags={initiativeTagsData}
          />
        );
      })}
    </div>
  );
};
